import React, { useState, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { Modal, Button } from 'antd';
import cls from 'classnames';

import { getPortal } from '@/components/PortalContext';
import ThrottleButton from '@/components/Reservation/ThrottleButton';

import styles from './index.module.scss';

interface IConfig {
  title: string;
  closable?: boolean;
  className?: string;
  content: React.ReactNode | string;
  onCancel?: () => void;
  cancelText?: React.ReactNode | string;
  onConfirm: () => void;
  confirmText?: React.ReactNode | string;
  confirmLoading?: boolean;
}

interface IProps extends IConfig {
  onClose: () => void;
  innerRef;
}

const ModalWrapper: React.FC<IProps> = (props) => {
  const {
    innerRef,
    title,
    closable,
    content,
    className,
    cancelText,
    confirmText,
    confirmLoading = false,
    onCancel,
    onConfirm,
    onClose,
  } = props;
  const [visible, setVisible] = useState(false);

  useImperativeHandle(
    innerRef,
    () => ({
      closeModal: () => setVisible(false),
    }),
    [],
  );

  useEffect(() => {
    setVisible(true);
  }, []);

  const showCloseIcon = closable ?? !!title;

  return (
    <Modal
      wrapClassName={cls(styles.confirmPortalModal, className)}
      destroyOnClose
      centered
      maskClosable={false}
      afterClose={onClose}
      open={visible}
      title={title}
      onCancel={onCancel}
      closable={showCloseIcon}
      focusTriggerAfterClose={false}
      confirmLoading={confirmLoading}
      footer={[
        cancelText ? (
          <Button onClick={onCancel} key="cancel">
            {cancelText}
          </Button>
        ) : null,
        confirmText ? (
          <ThrottleButton type="primary" onClick={onConfirm} key="confirm" loading={confirmLoading}>
            {confirmText}
          </ThrottleButton>
        ) : null,
      ]}
    >
      {content}
    </Modal>
  );
};

const usePortalModal = () => {
  const modalRef = useRef();
  const modal = useMemo(() => {
    let nodeId: string;

    const closeDrawer = () => {
      if (nodeId) {
        getPortal().removePortalNode(nodeId);
      }
    };

    const showDrawer: (config: IConfig) => void = (config) => {
      nodeId = getPortal().renderPortalNode(() => (
        <ModalWrapper {...config} innerRef={modalRef} onClose={closeDrawer} />
      ));
    };

    return {
      showDrawer: (config: IConfig) => {
        modalRef.current = null;
        closeDrawer();
        showDrawer(config);
      },
      closeDrawer: () => {
        const modalInst = modalRef.current;
        if (modalInst) {
          (modalInst as any).closeModal();
        }
      },
    };
  }, []);

  useEffect(() => {
    return () => {
      modal.closeDrawer();
    };
  }, [modal]);

  return modal;
};

export default usePortalModal;

export type PortalInstance = ReturnType<typeof usePortalModal>;
